/**
 * OpenSearchFieldMapper - Utility for parsing and working with OpenSearch field mappings
 *
 * This utility provides methods to parse OpenSearch mappings, determine field types,
 * and generate field options for UI components like dropdowns.
 */

import { snakeToCamelCase, camelToSnakeCase } from './reporting/string-case-conversion';

export interface FieldOption {
  key: string;
  label: string;
  type: string;
  group?: string; // Category/section for organizing column selection
  isDate?: boolean;
  isTimestamp?: boolean;
  isAggregatable?: boolean; // True if field can be aggregated (e.g., for reporting or analytics)
  adminOnly?: boolean; // True if field should only be shown in admin contexts
}

export interface OpenSearchMapping {
  [indexName: string]: {
    mappings: {
      properties: {
        [fieldName: string]: {
          type: string;
          fields?: Record<string, { type: string }>;
          properties?: Record<string, unknown>;
        };
      };
    };
  };
}

export class OpenSearchFieldMapper {
  /**
   * List of fields that should only be shown in admin contexts
   * These are typically configuration or workflow fields that are useful for admins
   * but not needed for employee self-service
   */
  private static readonly ADMIN_ONLY_FIELDS = new Set([
    'approverCanEditApplication',
    'programApprovalRequired',
    'courseApprovalRequired',
    'courseGradeRequired',
    'evidenceApprovalRequired',
    'indexedAt',
    'documentCreatedAt',
    // Add other admin-only fields here
  ]);

  /**
   * Custom label mappings for fields that don't follow standard naming conventions
   * Maps camelCase field names (after prefix stripping) to custom display labels
   * Used when the auto-generated label from camelCase conversion doesn't match desired UI text
   */
  private static readonly FIELD_LABEL_MAP: Record<string, string> = {
    last: 'Date', // For column selector (payment prefix stripped)
    first: 'Date', // For column selector (payment prefix stripped)
  };

  /**
   * Remove OpenSearch type suffixes from a field name to get the base field name
   * Only handles camelCase format - snake_case should be converted upstream
   *
   * NOTE: If you see snake_case field names here, that's a bug! All field names
   * should be converted to camelCase during query creation or data loading.
   *
   * @example
   * removeFieldTypeSuffixes('employeeNameKeyword') // => 'employeeName'
   * removeFieldTypeSuffixes('isActiveBoolean') // => 'isActive'
   * removeFieldTypeSuffixes('createdAtDate') // => 'createdAt'
   * removeFieldTypeSuffixes('name.keyword') // => 'name'
   */
  static removeFieldTypeSuffixes(fieldName: string): string {
    return (
      fieldName
        // Remove .keyword suffix first (for text fields with keyword subfields)
        .replace(/\.keyword$/, '')
        // Remove camelCase type suffixes
        .replace(/Keyword$/, '')
        .replace(/Text$/, '')
        .replace(/Date$/, '')
        .replace(/Boolean$/, '')
        .replace(/Integer$/, '')
        .replace(/Long$/, '')
        .replace(/Float$/, '')
        .replace(/Double$/, '')
        .replace(/Short$/, '')
        .replace(/Byte$/, '')
    );
  }

  /**
   * Check if a field is admin-only
   */
  static isAdminOnlyField(fieldName: string): boolean {
    const baseFieldName = this.removeFieldTypeSuffixes(fieldName);
    return this.ADMIN_ONLY_FIELDS.has(baseFieldName);
  }

  /**
   * Parse OpenSearch mapping data into field options suitable for UI components
   *
   * NOTE: As of the backend change, field names are returned in camelCase (e.g., 'employeeNameKeyword')
   * This matches the format of response data, so no conversion is needed when reading item properties.
   */
  static parseMapping(mappingData: OpenSearchMapping): FieldOption[] {
    if (!mappingData || typeof mappingData !== 'object') {
      console.warn('Invalid mapping data provided');
      return [];
    }

    const fields: FieldOption[] = [];

    // Get the first index mapping (assuming single index for now)
    const indexKey = Object.keys(mappingData)[0];
    if (!indexKey || !mappingData[indexKey]?.mappings?.properties) {
      console.warn('No valid mapping properties found');
      return [];
    }

    const properties = mappingData[indexKey].mappings.properties;

    // Parse each field in the mapping
    // Field names are now in camelCase (e.g., 'employeeNameKeyword', 'payrollId')
    Object.entries(properties).forEach(([fieldName, fieldConfig]) => {
      if (OpenSearchFieldMapper.isFieldSuitableForAggregation(fieldName, fieldConfig)) {
        const label = OpenSearchFieldMapper.formatFieldLabel(fieldName);
        const isAdminOnly = OpenSearchFieldMapper.isAdminOnlyField(fieldName);
        const group = isAdminOnly
          ? 'Admin Configuration'
          : OpenSearchFieldMapper.getFieldGroup(fieldName);
        const isDate = fieldConfig.type === 'date';
        const isTimestamp =
          fieldConfig.type !== 'date' && OpenSearchFieldMapper.isTimestampField(fieldName);

        // For text fields with keyword sub-fields, only include the keyword version
        if (fieldConfig.type === 'text' && fieldConfig.fields?.keyword) {
          fields.push({
            key: `${fieldName}.keyword`,
            label, // Use clean label without technical suffixes
            type: 'keyword',
            group,
            isDate: false,
            isTimestamp: false,
            adminOnly: isAdminOnly,
          });
        } else {
          // For non-text fields or text fields without keyword sub-fields
          fields.push({
            key: fieldName,
            label,
            type: fieldConfig.type,
            group,
            isDate,
            isTimestamp,
            adminOnly: isAdminOnly,
          });
        }
      }
    });

    // Sort fields alphabetically by label
    return fields.sort((a, b) => a.label.localeCompare(b.label));
  }

  /**
   * Determine if a field is suitable for aggregation
   */
  static isFieldSuitableForAggregation(
    fieldName: string,
    fieldConfig: { type: string; fields?: Record<string, unknown> }
  ): boolean {
    const { type } = fieldConfig;

    // Skip internal/system fields
    if (fieldName.startsWith('_') || fieldName.includes('embedding')) {
      return false;
    }

    // Include these types for aggregation
    const goodTypes = ['keyword', 'boolean', 'date', 'long', 'integer', 'short', 'byte'];
    if (goodTypes.includes(type)) {
      return true;
    }

    // Include text fields that have a keyword subfield
    if (type === 'text' && fieldConfig.fields?.keyword) {
      return true;
    }

    return false;
  }

  /**
   * Check if a field name suggests it's a timestamp field
   */
  static isTimestampField(fieldName: string): boolean {
    const timestampPatterns = [
      /at$/i, // ends with 'at' (createdAt, updatedAt, etc.)
      /time/i, // contains 'time'
      /date/i, // contains 'date'
      /_at$/i, // ends with '_at'
      /_date$/i, // ends with '_date'
      /_time$/i, // ends with '_time'
    ];

    return timestampPatterns.some((pattern) => pattern.test(fieldName));
  }

  /**
   * Format field names into readable labels with context awareness
   * Provides two label formats:
   * - Short labels for column selection UI (strips all prefixes)
   * - Full labels for table headers (keeps necessary context)
   *
   * NOTE: Field names are now in camelCase (e.g., 'employeeNameKeyword', 'tasProgramTemplateFieldProgramNameKeyword')
   *
   * @param fieldName - The OpenSearch field name in camelCase
   * @param options - Formatting options
   * @returns Formatted label string
   */
  static formatFieldLabel(
    fieldName: string,
    options: { keepContext?: boolean; group?: string } = {}
  ): string {
    const { keepContext = false } = options;
    let contextPrefix = '';

    // Step 1 & 2: Remove type suffixes using utility method
    let clean = this.removeFieldTypeSuffixes(fieldName);

    // Step 3: Extract context and remove prefixes
    // Handle camelCase prefixes
    if (keepContext) {
      // Keep context for ambiguous fields
      if (clean.match(/^tasProgramTemplateField/)) {
        contextPrefix = 'Program: ';
        clean = clean.replace(/^tasProgramTemplateField/, '');
      } else if (clean.match(/^tasProgramInstance/)) {
        contextPrefix = 'Program: ';
        clean = clean.replace(/^tasProgramInstance/, '');
      } else if (clean.match(/^tasProgramTemplate/)) {
        contextPrefix = 'Program: ';
        clean = clean.replace(/^tasProgramTemplate/, '');
      } else if (clean.match(/^tasApplication/)) {
        contextPrefix = 'Application: ';
        clean = clean.replace(/^tasApplication/, '');
      } else if (clean.match(/^tasCourse/)) {
        contextPrefix = 'Course: ';
        clean = clean.replace(/^tasCourse/, '');
      } else if (clean.match(/^payment/)) {
        contextPrefix = 'Payment: ';
      } else {
        // For fields without explicit prefixes, infer context from common patterns
        // This handles cases where OpenSearch returns fields like "createdAt", "updatedAt", "state"
        // that belong to different entities but don't have prefixes
        const ambiguousFields = [
          'createdAt',
          'created',
          'updatedAt',
          'updated',
          'state',
          'status',
          'id',
          'name',
          'type',
        ];

        const cleanLower = clean.toLowerCase();
        const isAmbiguous = ambiguousFields.some((field) => cleanLower === field.toLowerCase());

        if (isAmbiguous) {
          // Try to infer context from the original field name before cleaning
          if (fieldName.match(/program/i) || fieldName.match(/tas_program_instance/i)) {
            contextPrefix = 'Program: ';
          } else if (fieldName.match(/application/i) || fieldName.match(/tas_application/i)) {
            contextPrefix = 'Application: ';
          } else if (fieldName.match(/course/i)) {
            contextPrefix = 'Course: ';
          } else if (fieldName.match(/payment/i)) {
            contextPrefix = 'Payment: ';
          } else if (fieldName.match(/employee/i)) {
            contextPrefix = 'Employee: ';
          }
        }
      }
    } else {
      // Strip all prefixes for column selection UI (camelCase versions)
      clean = clean
        .replace(/^tasProgramTemplateField/, '')
        .replace(/^tasProgramInstanceField/, '') // Legacy/incorrect prefix
        .replace(/^tasProgramInstance/, '')
        .replace(/^tasProgramTemplate/, '')
        .replace(/^tasApplicationField/, '') // Legacy/incorrect prefix
        .replace(/^tasApplication/, '')
        .replace(/^tasCourse/, '')
        .replace(/^tas/, '')
        .replace(/^payment/, '') // Strip payment prefix for column selector
        .replace(/^field/, ''); // Strip standalone "field" prefix (legacy fields)
    }

    // Step 4a: Handle special field name mappings
    // Check if this field has a custom label mapping
    if (this.FIELD_LABEL_MAP[clean]) {
      return contextPrefix + this.FIELD_LABEL_MAP[clean];
    }

    // Step 4: Convert camelCase to Title Case
    // Split on capital letters, join with spaces, and capitalize each word
    const titleCased = clean
      .replace(/([A-Z])/g, ' $1') // Add space before capitals
      .trim()
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

    return contextPrefix + titleCased;
  }

  /**
   * Get the group/category for a field (for organizing column selection)
   * NOTE: Field names are now in camelCase
   */
  static getFieldGroup(fieldName: string): string {
    // Handle camelCase field names
    if (fieldName.match(/^tasProgramTemplateField/)) {
      return 'Program Details';
    } else if (fieldName.match(/^tasProgramInstance/)) {
      return 'Program Instance';
    } else if (fieldName.match(/^tasProgramTemplate/)) {
      return 'Program Template';
    } else if (fieldName.match(/^tasApplication/)) {
      return 'Application';
    } else if (fieldName.match(/^tasCourse/)) {
      return 'Courses';
    } else if (fieldName.match(/^payment/)) {
      return 'Payments';
    } else if (fieldName.match(/^employee/)) {
      return 'Employee';
    } else if (fieldName.match(/^company/)) {
      return 'Company';
    }
    return 'Other';
  }

  /**
   * Get fallback fields for known datasets when mapping fails
   */
  static getFallbackFields(dataset: string): FieldOption[] {
    switch (dataset) {
      case 'tas-program-instances':
        return [
          // New standardized field names (Phase 1)
          { key: 'employee_name_keyword', label: 'Employee Name', type: 'text' },
          {
            key: 'tas_program_template_field_program_name_keyword',
            label: 'Program Name',
            type: 'text',
          },
          {
            key: 'tas_program_template_field_program_specialization_keyword',
            label: 'Program Specialization',
            type: 'text',
          },
          {
            key: 'tas_program_instance_created_at_date',
            label: 'Program Created At',
            type: 'date',
            isDate: true,
          },
          { key: 'tas_program_instance_id_keyword', label: 'Program Instance ID', type: 'keyword' },
          { key: 'tas_program_instance_state_keyword', label: 'Program State', type: 'keyword' },
          { key: 'tas_program_template_id_keyword', label: 'Program Template ID', type: 'keyword' },
          { key: 'tas_program_template_type_keyword', label: 'Program Type', type: 'keyword' },
          {
            key: 'tas_program_template_field_voucher_program_boolean',
            label: 'Voucher Program',
            type: 'boolean',
          },

          // Other fields
          { key: 'application_state', label: 'Application State', type: 'keyword' },
          { key: 'created_at', label: 'Created At', type: 'date', isDate: true },
          { key: 'updated_at', label: 'Updated At', type: 'date', isDate: true },
          { key: 'employee_id', label: 'Employee ID', type: 'keyword' },
        ];
      case 'pslf-forms':
        return [
          { key: 'form_status', label: 'Form Status', type: 'keyword' },
          { key: 'form_type', label: 'Form Type', type: 'keyword' },
          { key: 'created_at', label: 'Created At', type: 'date', isDate: true },
          { key: 'updated_at', label: 'Updated At', type: 'date', isDate: true },
        ];
      case 'people':
        return [
          { key: 'company_name', label: 'Company Name', type: 'keyword' },
          { key: 'job_title', label: 'Job Title', type: 'keyword' },
          { key: 'department', label: 'Department', type: 'keyword' },
          { key: 'created_at', label: 'Created At', type: 'date', isDate: true },
        ];
      default:
        return [
          { key: 'id', label: 'ID', type: 'keyword' },
          { key: 'created_at', label: 'Created At', type: 'date', isDate: true },
        ];
    }
  }

  /**
   * Get the default field for a given dataset
   * Returns camelCase field names to match what the mapping endpoint returns
   */
  static getDefaultField(dataset: string): string {
    switch (dataset) {
      case 'tas-program-instances':
        return 'tasApplicationStateKeyword';
      case 'pslf-forms':
        return 'formStatusKeyword';
      case 'people':
        return 'companyName';
      default:
        return 'id';
    }
  }

  /**
   * Convert field name to different cases
   * @deprecated Use snakeToCamelCase from string-case-conversion instead
   */
  static toCamelCase(str: string): string {
    return snakeToCamelCase(str);
  }

  /**
   * @deprecated Use camelToSnakeCase from string-case-conversion instead
   */
  static toSnakeCase(str: string): string {
    return camelToSnakeCase(str);
  }

  /**
   * Get all possible aggregation key variations for a field name
   * This helps with backend inconsistencies in key naming
   */
  static getAggregationKeyVariations(fieldName: string): string[] {
    const baseKey = `${fieldName}_agg`;
    return [
      baseKey,
      snakeToCamelCase(baseKey),
      `${fieldName}Agg`,
      snakeToCamelCase(fieldName) + 'Agg',
    ];
  }
}
